/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';

import { Margins } from 'src/design-system/style-types';

import { Loading } from '../Loading';
import { Text } from '../Text';
import { container, inputCss, loading, loadingContainer, textCss, toggleCss } from './Toggle.style';

type OnChange = ({ name, checked }: { name: string; checked: boolean }) => void;

export interface RequiredProps {
  name: string;
}

export interface DefaultProps {
  className: string;
  leftLabel: string | React.ReactNode;
  rightLabel: string | React.ReactNode;
  checked: boolean;
  onChange: OnChange;
  disabled: boolean;
  tabIndex: number;
  dark: boolean;
  marginBottom: Margins;
  fullWidth: boolean;
  loading: boolean;
}

const defaultProps: DefaultProps = {
  className: '',
  leftLabel: '',
  rightLabel: '',
  onChange: () => {},
  checked: false,
  disabled: false,
  tabIndex: 0,
  dark: false,
  marginBottom: '0',
  fullWidth: false,
  loading: false,
};

const handleKeyDown =
  (props: DefaultProps & RequiredProps) =>
  ({ key }: React.KeyboardEvent<HTMLLabelElement>) => {
    const { disabled, checked, onChange } = props;
    if (key === ' ' && !disabled) {
      onChange({ name: props.name, checked: !checked });
    }
  };

const _Toggle = (props: DefaultProps & RequiredProps) => {
  const { leftLabel, rightLabel, checked, onChange, disabled, name, tabIndex, fullWidth } = props;

  return (
    <div
      css={container(props)}
      onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();
        !disabled && onChange({ name, checked: !checked });
      }}
      tabIndex={0}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
    >
      {leftLabel && (
        <Text
          css={textCss}
          data-qa="two-labels-left"
          color={checked && rightLabel ? 'grey' : 'dark'}
          tag="label"
          marginBottom="0"
          htmlFor={name}
          fullWidth={fullWidth}
        >
          {leftLabel}
        </Text>
      )}
      <input
        css={inputCss}
        type="checkbox"
        checked={checked}
        id={name}
        name={name}
        disabled={disabled}
        data-qa="input"
        readOnly
      />
      <label
        aria-label={name}
        aria-checked={checked}
        data-qa="label"
        tabIndex={tabIndex}
        onKeyDown={handleKeyDown(props)}
        htmlFor={name}
        css={toggleCss(props)}
      />
      {props.loading && (
        <div css={loadingContainer}>
          <Loading css={loading} size="lg" />
        </div>
      )}
      {rightLabel && (
        <Text
          css={textCss}
          data-qa="two-labels-right"
          color={!checked && leftLabel ? 'grey' : 'dark'}
          tag="label"
          marginBottom="0"
          htmlFor={name}
          fullWidth={fullWidth}
        >
          {rightLabel}
        </Text>
      )}
    </div>
  );
};

_Toggle.defaultProps = defaultProps;

export const Toggle = React.memo(_Toggle);
